import { useContext, useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import {
  CYRILLIC_REGEXP,
  getAccountNumberRegExp
} from 'components/common/PaymentForm/ProviderForms/ModelValidators/utils';
import i18nContext from 'components/i18n-context';
import ValidateAccountNumberModel from 'components/NewPaymentFormV2/models/ValidateAccountNumberModel';
import { CURRENCIES } from 'services/currenciesConstant';
import { formatSortCode, isCryptoProvider, amountFormattedValue } from 'services/utils';
import { CRYPTO_CURRENCY_PRECISION } from 'services/currenciesConstant';
import { filterPaymentAccounts } from 'components/common/PaymentForm/utils';
import { getAccountTitleWithFormatting } from 'components/utils';
import Input from 'uikit/Input/Input';
import Button from 'uikit/Button/Button';
import { InputDropDown } from 'uikit/InputDropDown/InputDropDown';
import './AccountDetailsStep.scss';
import { getNewPaymentFormStepErrorMessage } from 'components/NewPaymentFormV2/utils/getNewPaymentFormStepErrorMessage';

const AccountDetailsStep = ({ customerNumber, userAccounts, newPaymentStore }) => {
  const i18n = useContext(i18nContext);
  const providerType = newPaymentStore.currentAccount?.transfer_provider;
  const [isCryptoCurrency, setIsCryptoCurrency] = useState(isCryptoProvider(providerType));

  const form = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      walletNumber: newPaymentStore.currentAccount?.wallet_number || '',
      accountNumber:
        newPaymentStore.accountDetailsStep?.accountNumber ||
        newPaymentStore.accountDetailsStep?.iban ||
        newPaymentStore.accountDetailsStep?.cryptoAddress ||
        '',
      sortCode: newPaymentStore.accountDetailsStep?.sortCode || ''
    },
    validationSchema: Yup.object().shape({
      walletNumber: Yup.string().trim().required(i18n.getMessage('client.validation.message.isEmpty')),
      accountNumber: Yup.string()
        .trim()
        .required(i18n.getMessage('client.validation.message.isEmpty'))
        .max(50, i18n.getMessage('client.validation.message.maxSize', { size: 50 }))
        .min(7, i18n.getMessage('client.validation.message.minSize', { size: 7 }))
        .matches(getAccountNumberRegExp(), {
          message: i18n.getMessage('transfer.form.error.accountNumber')
        }),
      sortCode: Yup.string()
    }),
    onSubmit: async (values) => {
      const selectedAccount = newPaymentStore.currentAccount;
      const currency = selectedAccount?.currency;
      const network = selectedAccount?.network || '';
      const requestData = ValidateAccountNumberModel({
        ...newPaymentStore.newPaymentFormData,
        ...values,
        currency,
        network
      });
      const newPaymentFormData = {
        ...values,
        currency,
        network,
        isCryptoPayment: isCryptoCurrency
      };

      if (isCryptoCurrency) {
        newPaymentFormData.walletAddress = values.accountNumber;
      }

      newPaymentStore.updateAccountDetailsStep(newPaymentFormData);

      await newPaymentStore.validateAccountDetails(customerNumber, requestData, isCryptoCurrency);
    }
  });

  const { values, errors, handleSubmit, handleChange, setFieldValue } = form;

  const handleAccountChange = (name, walletNumber) => {
    const selectedAccount = filterPaymentAccounts(userAccounts || []).find(
      (account) => account.wallet_number === walletNumber
    );

    if (selectedAccount) {
      newPaymentStore.setAccountInfo(selectedAccount);
      setFieldValue('walletNumber', walletNumber);
      setIsCryptoCurrency(isCryptoProvider(selectedAccount?.transfer_provider));
    }
  };

  const onSortCodeChange = (e) => {
    const inputValue = e.target.value;
    let numericValue = formatSortCode(inputValue);

    setFieldValue('sortCode', numericValue);
  };

  // Set initial account on mount
  useEffect(() => {
    const paymentAccounts = filterPaymentAccounts(userAccounts || []);
    if (paymentAccounts.length > 0 && !newPaymentStore.currentAccount) {
      handleAccountChange('', paymentAccounts[0].wallet_number);
    }
  }, [userAccounts]);

  return (
    <form onSubmit={form.handleSubmit} className={'new-payment-account-details-step'}>
      <div
        // eslint-disable-next-line max-len
        className={`new-payment-account-details-step-inputs-wrapper ${newPaymentStore.currentAccount?.currency === CURRENCIES.GBP.code ? 'new-payment-gbp-max-width-wrapper' : ''}`}
      >
        {values.walletNumber && (
          <>
            {!isCryptoCurrency && (
              <Input
                className={'new-payment-account-details-step-input'}
                name={'accountNumber'}
                label={i18n.getMessage('templates.form.accountNumber.label')}
                placeholder={i18n.getMessage('templates.form.accountNumber.placeholder')}
                value={values.accountNumber}
                error={
                  errors.accountNumber ||
                  getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'accountNumber' || 'iban')
                }
                onChange={handleChange}
                isRequired
              />
            )}
            {newPaymentStore.currentAccount?.currency === CURRENCIES.GBP.code && (
              <Input
                className={'new-payment-account-details-step-input'}
                name={'sortCode'}
                label={i18n.getMessage('templates.form.sortCode.label')}
                placeholder={i18n.getMessage('templates.form.sortCode.placeholder')}
                value={values.sortCode}
                error={errors.sortCode || getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'sortCode')}
                onChange={onSortCodeChange}
                max={8}
              />
            )}

            {isCryptoCurrency && (
              <Input
                className={'new-payment-account-details-step-input new-payment-crypto-wallet-address-input'}
                name={'accountNumber'}
                label={i18n.getMessage('templates.form.walletAddress.label')}
                placeholder={i18n.getMessage('templates.form.walletAddress.placeholder')}
                value={values.accountNumber}
                error={
                  errors.accountNumber ||
                  getNewPaymentFormStepErrorMessage(i18n, newPaymentStore.error, 'accountNumber')
                }
                onChange={handleChange}
                isRequired
              />
            )}
          </>
        )}
      </div>
      <Button
        className={'new-payment-account-details-step-next-button'}
        type={'primary'}
        roleType={'submit'}
        onClick={handleSubmit}
        isDisabled={newPaymentStore.isLoading}
      >
        {i18n.getMessage('templates.form.button.next')}
      </Button>
    </form>
  );
};

AccountDetailsStep.propTypes = {
  customerNumber: PropTypes.string,
  userAccounts: PropTypes.array,
  newPaymentStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  newPaymentStore: stores.newPaymentStore
}))(observer(AccountDetailsStep));
